import React from 'react';
import { Heart, MessageCircle, ExternalLink, MessageSquare, ChevronRight } from 'lucide-react';
import { FaFacebook, FaInstagram } from 'react-icons/fa';

export const SocialPost = ({ post, onOpen }) => {
    const isInstagram = post.platform === 'instagram';
    const postDate = new Date(post.timestamp).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });

    const commentsCount = post.comments_count || 0;

    return (
        <div className="bg-white border border-slate-100 p-5 rounded-3xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 group flex flex-col h-full shadow-sm relative overflow-hidden">
            {/* Platform Accent Bar */}
            <div className={`absolute top-0 left-0 right-0 h-1 ${isInstagram ? 'bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500' : 'bg-blue-600'}`}></div>

            {/* Header */}
            <div className="flex items-center justify-between mb-4 mt-1">
                <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl text-white shadow-md ${isInstagram ? 'bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-500' : 'bg-[#1877F2]'}`}>
                        {isInstagram ? <FaInstagram size={20} /> : <FaFacebook size={20} />}
                    </div>
                    <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                            {postDate}
                        </div>
                    </div>
                </div>
                <a 
                    href={post.permalink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 bg-slate-50 text-slate-300 hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all"
                >
                    <ExternalLink size={14} />
                </a>
            </div>

            {/* Media Container */}
            {post.media_url && (
                <div 
                    className="relative aspect-video mb-5 overflow-hidden rounded-2xl bg-slate-50 shadow-inner flex items-center justify-center cursor-pointer group/img"
                    onClick={onOpen}
                >
                    <img 
                        src={post.media_url} 
                        alt="Post media" 
                        className="w-full h-full object-cover group-hover/img:scale-110 transition-transform duration-700"
                        onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 flex items-center justify-center transition-all">
                        <MessageSquare className="text-white opacity-0 group-hover/img:opacity-100 scale-50 group-hover/img:scale-100 transition-all drop-shadow-lg" size={24} />
                    </div>
                </div>
            )}

            {/* Content Area */}
            <div className="space-y-4 flex-1">
                <p className="text-sm text-slate-600 leading-relaxed font-medium line-clamp-3">
                    {post.caption || "No caption provided"}
                </p>

                {/* Combined Interaction Section */}
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-6 pb-2 border-b border-slate-50">
                        <div className="flex items-center gap-2">
                           <Heart size={16} className="text-brand-orange fill-brand-orange" />
                           <span className="text-xs font-black text-slate-800">{(post.likes || 0).toLocaleString()}</span>
                        </div>
                        <div className="flex items-center gap-2">
                           <MessageCircle size={16} className="text-brand-blue fill-brand-blue" />
                           <span className="text-xs font-black text-slate-800">{(post.comments_count || 0).toLocaleString()}</span>
                        </div>
                    </div>

                    {/* btn-primary Styled Button (as per Leads reference) */}
                    <button 
                        onClick={onOpen}
                        disabled={commentsCount === 0}
                        className={`btn-primary w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs uppercase font-black tracking-widest shadow-lg shadow-blue-100/50 transition-all active:scale-95 ${commentsCount === 0 ? 'opacity-40 grayscale pointer-events-none' : ''}`}
                    >
                        <MessageCircle size={14} />
                        Read Discussion
                        <ChevronRight size={14} className="ml-auto" />
                    </button>
                    
                    {post.comments && post.comments.length > 0 && (
                        <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100 cursor-pointer" onClick={onOpen}>
                            <p className="text-[10px] text-slate-500 line-clamp-1 italic text-center">
                               Latest: "{post.comments[0].message}"
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
